import { supabase } from '../../services/supabase';
import { WorkerProject, WorkerDocument, WorkerApiResponse } from '../types/worker.types';

export class WorkerService {
  private static instance: WorkerService;

  public static getInstance(): WorkerService {
    if (!WorkerService.instance) {
      WorkerService.instance = new WorkerService();
    }
    return WorkerService.instance;
  }

  async validateToken(token: string): Promise<WorkerApiResponse<WorkerProject>> {
    try {
      console.log('Validating token:', token);
      
      // Debug: voir tous les tokens en base
      const { data: allProjects } = await supabase
        .from('projects')
        .select('access_token, name, is_active');
      console.log('All tokens in database:', allProjects);
      
      const { data: project, error } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          description,
          address,
          access_token,
          config,
          logo_url,
          is_active,
          company_id
        `)
        .eq('access_token', token)
        .maybeSingle();

      if (error) {
        console.error('Token validation error:', error);
        console.log('Token searched:', token);
        return {
          data: null,
          error: {
            type: 'invalid_token',
            message: 'Token invalide ou projet non trouvé',
            details: error.message
          }
        };
      }

      if (!project) {
        console.log('No project found with token:', token);
        return {
          data: null,
          error: {
            type: 'invalid_token',
            message: 'Token invalide ou projet non trouvé',
            details: 'Aucun projet trouvé avec ce token'
          }
        };
      }

      console.log('Project found:', project);

      if (!project.is_active) {
        console.log('Project is not active:', project.is_active);
        return {
          data: null,
          error: {
            type: 'project_inactive',
            message: 'Ce projet n\'est plus actif',
            details: 'Le projet a été désactivé par l\'administrateur'
          }
        };
      }

      // Get company info
      const { data: company } = await supabase
        .from('companies')
        .select('id, name, logo_url')
        .eq('id', project.company_id)
        .single();

      // Log access
      await this.logAccess(project.id);

      console.log('Returning project successfully:', project);
      return {
        data: {
          ...project,
          company: company || undefined
        } as WorkerProject,
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: {
          type: 'network_error',
          message: 'Erreur de connexion',
          details: error instanceof Error ? error.message : 'Erreur inconnue'
        }
      };
    }
  }

  async getProjectDocuments(projectId: string): Promise<WorkerApiResponse<WorkerDocument[]>> {
    try {
      const { data: documents, error } = await supabase
        .from('documents')
        .select(`
          id,
          project_id,
          name,
          file_url,
          file_type,
          file_size,
          category,
          tags,
          thumbnail_url,
          created_at
        `)
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) {
        return {
          data: null,
          error: {
            type: 'network_error',
            message: 'Erreur lors du chargement des documents',
            details: error.message
          }
        };
      }

      return {
        data: documents as WorkerDocument[],
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: {
          type: 'network_error',
          message: 'Erreur de connexion',
          details: error instanceof Error ? error.message : 'Erreur inconnue'
        }
      };
    }
  }

  async getDocumentsByCategory(projectId: string, category: string): Promise<WorkerApiResponse<WorkerDocument[]>> {
    try {
      const { data: documents, error } = await supabase
        .from('documents')
        .select(`
          id,
          project_id,
          name,
          file_url,
          file_type,
          file_size,
          category,
          tags,
          thumbnail_url,
          created_at
        `)
        .eq('project_id', projectId)
        .eq('category', category)
        .order('created_at', { ascending: false });

      if (error) {
        return {
          data: null,
          error: {
            type: 'network_error',
            message: 'Erreur lors du chargement des documents',
            details: error.message
          }
        };
      }

      return {
        data: documents as WorkerDocument[],
        error: null
      };
    } catch (error) {
      return {
        data: null,
        error: {
          type: 'network_error',
          message: 'Erreur de connexion',
          details: error instanceof Error ? error.message : 'Erreur inconnue'
        }
      };
    }
  }

  private async logAccess(projectId: string): Promise<void> {
    try {
      const userAgent = navigator.userAgent;
      const ipAddress = await this.getClientIP();

      await supabase
        .from('access_logs')
        .insert({
          project_id: projectId,
          ip_address: ipAddress,
          user_agent: userAgent,
          accessed_at: new Date().toISOString()
        });
    } catch (error) {
      console.warn('Failed to log access:', error);
    }
  }

  private async getClientIP(): Promise<string> {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      return 'unknown';
    }
  }

  async downloadDocument(fileUrl: string, fileName: string): Promise<void> {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      throw new Error('Erreur lors du téléchargement du document');
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getFileIcon(fileType: string): string {
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return 'file-text';
    if (type.includes('image')) return 'image';
    if (type.includes('video')) return 'video';
    if (type.includes('audio')) return 'music';
    if (type.includes('word') || type.includes('document')) return 'file-text';
    if (type.includes('excel') || type.includes('spreadsheet')) return 'file-spreadsheet';
    if (type.includes('powerpoint') || type.includes('presentation')) return 'presentation';
    return 'file';
  }
}

export const workerService = WorkerService.getInstance();